import React, { forwardRef, ReactNode, useCallback, useState } from 'react';
import { Button, Flex, useMediaQuery, useSafeLayoutEffect } from '@chakra-ui/react';
import classNames from 'classnames';
import Image, { StaticImageData } from 'next/image';
import Link from 'next/link';

import FullPage from '@/src/components/UI/FullPage';
import OpenGraph from '@/components/OpenGraph';
import PageTabContainer from '@/src/components/UI/PageTabContainer';
import Listing from '@/src/components/UI/Listing';
import ConsultantFormV3 from '@/src/components/feature/ConsultantForm/ConsultantFormV3';
import { QAItem } from '@/pages/abroad';
import { UserStory } from '@/src/components/UI/Home';
import useCollapse from '@/src/hooks/useCollapse';
import { CountryEnum, ListingDetail, UserStoryShowcaseEntity } from '@/swagger';

export interface SeminarPersona {
  image: StaticImageData;
  title: string;
  content: string;
}

export interface SeminarQAItem {
  question: string;
  answer: string;
  subItems?: Array<string>;
}

export interface SeminarStrategy {
  image: StaticImageData;
  imageFirst: boolean;
  title: string;
  body: ReactNode;
}

// Shared config for every "說明會獨立頁面" (standalone seminar landing page) — one per city/theme.
// Pages under pages/*-seminar/index.tsx supply this and render <SeminarLandingPage config={...} />.
export interface SeminarLandingPageConfig {
  // Used for the OG url/imageUrl (`${NEXT_PUBLIC_HOSTNAME}/${slug}`) — should match the page's route.
  slug: string;

  heroAlt: string;
  heroImage: StaticImageData;
  heroImageMobile: StaticImageData;
  // Full overlay headline shown on desktop, e.g. '《美國置產說明會》複製竹北奇蹟 入駐鳳凰城'.
  heroTitleDesktop: string;
  // Mobile shows the same headline split across two lines instead, e.g. ['美國置產說明會', '複製竹北奇蹟 入駐鳳凰城'].
  heroTitleMobileLines: [string, string];
  // Shown both in the desktop hero overlay and in the mobile below-banner block.
  heroSubhead: string;

  whyTabTitle: string;
  strategies: [SeminarStrategy, SeminarStrategy];

  listingsTabTitle: string;
  listingsHeadline: string;
  listingsDescription: ReactNode;
  // City names or keys for listingApi.search(), each scoped to its own state by the backend (see
  // funwooApis src/resource/listing/city-filter.ts). Use full names when a key is shared across
  // states (e.g. 'Brooklyn', not 'BRO').
  listingCities: Array<string>;
  listingsButtonLabel: string;

  buyersTabTitle: string;
  buyersHeadline: string;
  personas: [SeminarPersona, SeminarPersona, SeminarPersona];

  faqImage: StaticImageData;
  qaData: [SeminarQAItem, SeminarQAItem, SeminarQAItem];

  ogTitle: string;
  ogDescription: string;
  ogType: string;
}

interface Props {
  config: SeminarLandingPageConfig;
  listings: Array<ListingDetail>;
  userStories: Array<UserStoryShowcaseEntity>;
  buyLinkCities: Array<string>;
}

const Heading: React.FC<{ className?: string }> = ({ children, className }) => {
  return <p className={classNames('py-3', 'text-center text-headline2l font-medium', className)}>{children}</p>;
};

// Mobile shows the hero as a plain image (no overlaid text) — the headline/subhead/CTA render as
// their own white-background block right below it (see MobileHeroContent). Desktop keeps the
// classic overlay-on-photo treatment, so the text here is hidden below the `sm` breakpoint.
const Hero: React.FC<{ config: SeminarLandingPageConfig }> = ({ config }) => {
  return (
    <div className={classNames('relative w-full', 'aspect-[4/3] sm:aspect-auto sm:h-[640px]', 'overflow-hidden')}>
      {/* Next.js `<Image layout="fill">` sets its own inline `display:block` on the <img>, which
          beats a Tailwind visibility class placed directly on it — so the sm:hidden/hidden toggle
          has to live on a plain wrapping div instead (a hidden *parent* still hides everything
          inside, inline style or not). */}
      <div className={classNames('absolute inset-0', 'sm:hidden')}>
        <Image
          src={config.heroImageMobile.src}
          blurDataURL={config.heroImageMobile.blurDataURL}
          layout={'fill'}
          objectFit={'cover'}
          priority
          alt={config.heroAlt}
        />
      </div>
      <div className={classNames('absolute inset-0', 'hidden sm:block')}>
        <Image
          src={config.heroImage.src}
          blurDataURL={config.heroImage.blurDataURL}
          layout={'fill'}
          objectFit={'cover'}
          priority
          alt={config.heroAlt}
        />
      </div>
      <Flex
        display={{ base: 'none', sm: 'flex' }}
        direction={'column'}
        align={'start'}
        justify={'center'}
        className={classNames('absolute inset-0', 'px-6 lg:px-18', 'text-white')}
      >
        <p className={classNames('w-full', 'text-5xl font-medium leading-tight', 'text-shadow')}>
          {config.heroTitleDesktop}
        </p>
        <p className={classNames('mt-4', 'w-full', 'text-base')}>{config.heroSubhead}</p>
        <Button as={'a'} href={'#contact-us'} variant={'secondary'} mt={8} px={12} py={2} border={0} fontWeight={500}>
          說明會報名
        </Button>
      </Flex>
    </div>
  );
};

// Mobile-only counterpart to the hero overlay above — rendered as regular page content directly
// below the banner image, not inside FullPage's `banner` slot.
//
// The CTA label matches desktop's ("說明會報名") — the business asked for every registration entry
// on the site to use this wording, overriding the Figma source's reversed desktop word order.
const MobileHeroContent: React.FC<{ config: SeminarLandingPageConfig }> = ({ config }) => {
  const [firstLine, secondLine] = config.heroTitleMobileLines;
  return (
    <div className={classNames('sm:hidden', 'flex flex-col items-center', 'px-4 py-4 text-center')}>
      <p className={'text-2xl font-medium leading-[2rem]'}>
        {firstLine}
        <br />
        {secondLine}
      </p>
      <p className={'mt-2 text-base text-gray700'}>{config.heroSubhead}</p>
      <Button as={'a'} href={'#contact-us'} variant={'primary'} mt={4} px={8} py={2}>
        說明會報名
      </Button>
    </div>
  );
};

const StrategyBlock: React.FC<{ image: StaticImageData; imageFirst?: boolean; title: string }> = ({
  image,
  imageFirst = true,
  title,
  children,
}) => {
  const imageNode = (
    <div className={classNames('relative shrink-0', 'w-full sm:w-[40rem]', 'aspect-[3/2]')}>
      <Image src={image.src} blurDataURL={image.blurDataURL} layout={'fill'} objectFit={'cover'} alt={title} />
    </div>
  );
  const textNode = (
    <div className={classNames('flex-1', 'py-4 sm:py-0')}>
      <p className={'text-2xl sm:text-5xl font-medium text-center sm:text-left'}>{title}</p>
      <div className={'mt-4 text-gray700 sm:text-lg text-justify space-y-4'}>{children}</div>
    </div>
  );

  return (
    <div
      className={classNames(
        'flex flex-col sm:items-center',
        imageFirst ? 'sm:flex-row' : 'sm:flex-row-reverse',
        'gap-4 sm:gap-12',
        'py-8 sm:py-16 px-4 sm:px-10'
      )}
    >
      {imageNode}
      {textNode}
    </div>
  );
};

const WhySection = forwardRef<HTMLElement | null, { config: SeminarLandingPageConfig }>(({ config, ...props }, ref) => {
  // Mobile only shows the second strategy item after the visitor taps "查看更多" — desktop always
  // shows both. Mirrors the same collapse pattern as abroad.tsx's Services section.
  const [isMounted, setIsMounted] = useState(false);
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [aboveTablet] = useMediaQuery('(min-width: 768px)');

  useSafeLayoutEffect(() => {
    setIsMounted(true);
  }, []);

  const { height, triggerCollapsing, isCollapsed } = useCollapse({ collapsibleContainer: node }, [isMounted]);

  const [first, second] = config.strategies;

  return (
    <section {...props} id={'why-section'} ref={ref} style={{ scrollMarginTop: '66px' }}>
      <StrategyBlock image={first.image} imageFirst={first.imageFirst} title={first.title}>
        {first.body}
      </StrategyBlock>

      <div
        ref={setNode}
        style={{ height: aboveTablet && isMounted ? undefined : height }}
        className={classNames('overflow-hidden transition-[height] duration-300')}
      >
        <StrategyBlock image={second.image} imageFirst={second.imageFirst} title={second.title}>
          {second.body}
        </StrategyBlock>
      </div>

      <div className={'flex sm:hidden justify-center pb-8'}>
        <button
          type={'button'}
          onClick={() => triggerCollapsing()}
          className={'px-8 py-2 bg-black text-white text-base font-medium'}
        >
          {isCollapsed ? '查看更多' : '顯示更少'}
        </button>
      </div>
    </section>
  );
});

const FeaturedListings = forwardRef<
  HTMLElement | null,
  { config: SeminarLandingPageConfig; listings: Array<ListingDetail>; buyLinkCities: Array<string> }
>(({ config, listings, buyLinkCities, ...props }, ref) => {
  const [aboveTablet] = useMediaQuery('(min-width: 768px)');
  const visibleListings = aboveTablet ? listings.slice(0, 6) : listings.slice(0, 3);

  return (
    <section
      {...props}
      id={'listings'}
      ref={ref}
      style={{ scrollMarginTop: '66px' }}
      className={'px-4 sm:px-8 py-8 sm:py-16 bg-gray50'}
    >
      <Heading>{config.listingsHeadline}</Heading>
      <p className={'pb-4 text-center text-gray700'}>{config.listingsDescription}</p>

      <ConditionalListings listings={visibleListings} />

      <div className={'flex justify-center mt-8'}>
        <Link href={{ pathname: '/buy', query: { cities: buyLinkCities, country: CountryEnum.US } }} passHref>
          <Button as={'a'} variant={'primary'} w={{ base: '304px', sm: '304px' }} className={'py-2 text-center'}>
            {config.listingsButtonLabel}
          </Button>
        </Link>
      </div>
    </section>
  );
});

const ConditionalListings: React.FC<{ listings: Array<ListingDetail> }> = ({ listings }) => {
  if (listings.length === 0) return null;
  return (
    <div className={'grid grid-cols-1 sm:grid-cols-3 items-start gap-6 sm:gap-8 mb-8 max-w-full'}>
      {listings.map((listing) => (
        <div key={listing.id} className={'max-w-full sm:overflow-hidden'}>
          <Listing listing={listing} sizes={'100%'} />
        </div>
      ))}
    </div>
  );
};

const Buyers = forwardRef<HTMLElement | null, { config: SeminarLandingPageConfig }>(({ config, ...props }, ref) => {
  return (
    <section {...props} id={'buyers'} ref={ref} style={{ scrollMarginTop: '66px' }} className={'px-4 sm:px-8'}>
      <div className={'py-8 sm:py-16'}>
        <Heading className={'text-gray700'}>{config.buyersHeadline}</Heading>
      </div>
      <div className={'flex flex-col sm:flex-row gap-6 sm:gap-10 sm:justify-center sm:pb-16'}>
        {config.personas.map(({ image, title, content }) => (
          <div key={title} className={'flex flex-col gap-4 sm:w-[19rem]'}>
            <div className={'relative w-full aspect-[304/312]'}>
              <Image src={image.src} blurDataURL={image.blurDataURL} layout={'fill'} objectFit={'cover'} alt={title} />
            </div>
            <div className={'flex flex-col gap-2'}>
              <p className={'text-2xl leading-[2rem] font-medium text-black'}>{title}</p>
              <p className={'text-lg leading-[2rem] text-gray700'}>{content}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
});

// Headline/description are identical across every seminar page seen so far (Phoenix, New York) —
// hardcoded here rather than threaded through config until a page actually needs to vary them.
const Faq = forwardRef<HTMLElement | null, { config: SeminarLandingPageConfig }>(({ config, ...props }, ref) => {
  const [openCardIndex, setOpenCardIndex] = useState<number | null>(null);

  const onCardClick = useCallback((cardIndex: number) => {
    setOpenCardIndex((prev) => (prev === cardIndex ? null : cardIndex));
  }, []);

  return (
    <section
      {...props}
      id={'faq'}
      ref={ref}
      style={{ scrollMarginTop: '66px' }}
      className={'px-4 sm:px-8 py-8 sm:py-16 bg-gray50'}
    >
      <div className={'flex flex-col sm:flex-row items-center gap-8 sm:gap-8'}>
        <div className={'relative w-full sm:w-[42rem] aspect-[672/448] shrink-0'}>
          <Image
            src={config.faqImage.src}
            blurDataURL={config.faqImage.blurDataURL}
            layout={'fill'}
            objectFit={'cover'}
            alt={'美國置產服務'}
          />
        </div>
        <div className={'flex-1'}>
          <Heading className={'sm:text-left'}>美國置產服務</Heading>
          <p className={'text-gray700'}>
            服務範圍涵蓋美國主要城市，由當地執業房產顧問為您解答所有海外置產問題，量身打造一條龍服務。
          </p>
          <div className={'mt-4 space-y-4'}>
            {config.qaData.map(({ question, answer, subItems }, index) => (
              <QAItem
                key={question}
                question={question}
                answer={answer}
                subItems={subItems}
                showContactNote={false}
                onCardClick={() => onCardClick(index)}
                open={openCardIndex === index}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
});

const Contact = forwardRef<HTMLElement | null, { config: SeminarLandingPageConfig }>(({ config, ...props }, ref) => {
  return (
    <section {...props} id={'contact-us'} ref={ref} style={{ scrollMarginTop: '66px' }}>
      <ConsultantFormV3 embedded variant={'abroad'} />
    </section>
  );
});

const Testimonials = forwardRef<HTMLElement | null, { userStories: Array<UserStoryShowcaseEntity> }>(
  ({ userStories, ...props }, ref) => {
    // Not using the `-mx-4 sm:mx-0` bleed pattern here (unlike the other full-bleed sections on this
    // page) — UserStory embeds a Swiper carousel that measures its container width on mount, and
    // combining that with a negative-margin bleed caused real horizontal viewport overflow
    // (confirmed: horizontal scrollbar, content cut off on the right). Safer to accept a small inset
    // here than risk that again.
    return (
      <section {...props} id={'testimonials'} ref={ref} style={{ scrollMarginTop: '66px' }}>
        <UserStory stories={userStories} shouldOffset={false} />
      </section>
    );
  }
);

const SeminarLandingPage: React.FC<Props> = ({ config, listings, userStories, buyLinkCities }) => {
  return (
    <React.Fragment>
      <OpenGraph
        title={config.ogTitle}
        description={config.ogDescription}
        type={config.ogType}
        url={`${process.env.NEXT_PUBLIC_HOSTNAME}/${config.slug}`}
        imageUrl={`${process.env.NEXT_PUBLIC_HOSTNAME}/${config.slug}/hero.webp`}
      />
      <FullPage
        banner={<Hero config={config} />}
        belowBannerContent={<MobileHeroContent config={config} />}
        keepHeaderMode={false}
        hasBottomMargin={false}
        hasPadding={false}
        isNotSubPage
      >
        <PageTabContainer belowHeader className={''}>
          <WhySection config={config} data-title={config.whyTabTitle} />
          <FeaturedListings
            config={config}
            data-title={config.listingsTabTitle}
            listings={listings}
            buyLinkCities={buyLinkCities}
          />
          <Buyers config={config} data-title={config.buyersTabTitle} />
          <Faq config={config} data-title={'服務與常見問題'} />
          <Contact config={config} data-title={'說明會報名'} />
          <Testimonials data-title={'客戶故事'} userStories={userStories} />
        </PageTabContainer>
      </FullPage>
    </React.Fragment>
  );
};

export default SeminarLandingPage;
